// Position math for the round ⏱ floating time-entry button in Layout.js.
//
// Feedback #834 (James, 2026-09-30): "Time entry widget doesn't stay on screen
// according to how you have the window set up."  The button's position is
// stored as {right, bottom} pixel offsets from the window's edges.  Restoring
// those offsets unchecked meant a button dragged to the left of a wide window
// sat further from the right edge than a narrower window is wide — it rendered
// past the LEFT edge and could not be grabbed back (same for `bottom` past the
// TOP when the window got shorter).
//
// The fix keeps two positions apart:
//   - CHOSEN    — where the person dragged it.  Persisted in localStorage.
//   - DISPLAYED — the chosen position clamped into the current window,
//                 recomputed on every resize and NEVER persisted, so making the
//                 window big again puts the button back where they left it.
// Everything here is pure (except readFabViewport, which only reads) so the
// rules can be unit-tested without mounting Layout.

// Gap kept between the button and the window edge it is pushed against.
export const FAB_EDGE_MARGIN = 8

// Tailwind `right-8 bottom-24` (the pre-drag default) at a 16 px root.  Layout
// passes defaultFabPosition(rem) instead, because the app's font-size setting
// changes the root size (13-18 px) and the default has always been rem-based.
export const DEFAULT_FAB_POSITION = Object.freeze({ right: 32, bottom: 96 })

// w-12 h-12 at a 16 px root — only used when nothing better was measured.
export const DEFAULT_FAB_SIZE = 48

const isOffset = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0

// A usable saved/chosen position: an object with finite, non-negative
// `right` and `bottom`.  Anything else (null, strings, NaN, negatives, a
// missing key, an array) is treated as "never dragged".
export function isValidFabPosition (pos) {
  return !!pos && typeof pos === 'object' && !Array.isArray(pos) &&
    isOffset(pos.right) && isOffset(pos.bottom)
}

// The rem-based default (`right-8 bottom-24`) in pixels for a given root size.
export function defaultFabPosition (rem) {
  const r = (typeof rem === 'number' && Number.isFinite(rem) && rem > 0) ? rem : 16
  return { right: 2 * r, bottom: 6 * r }
}

// Parse the raw localStorage string.  Returns a clean {right, bottom} or null
// (null = use the default).  Never throws — a hand-edited or truncated value
// must not crash Layout, which wraps every page.
export function parseSavedFabPosition (raw) {
  if (typeof raw !== 'string' || !raw) return null
  let parsed
  try { parsed = JSON.parse(raw) } catch { return null }
  return isValidFabPosition(parsed) ? { right: parsed.right, bottom: parsed.bottom } : null
}

// Clamp a position into a window.  `pos` is the CHOSEN position (may be null /
// garbage → `fallback`, then DEFAULT_FAB_POSITION).  Each offset is kept
// between FAB_EDGE_MARGIN and (window extent − button size − margin).  When the
// window is too small for even that, the button pins to the margin — i.e. the
// bottom-right corner, where it lives by default.  An axis whose extent is
// unknown is left unclamped above (still at least the margin).
export function clampFabPosition (pos, viewport, size, fallback) {
  const base = isValidFabPosition(pos)
    ? pos
    : (isValidFabPosition(fallback) ? fallback : DEFAULT_FAB_POSITION)
  const s = (typeof size === 'number' && Number.isFinite(size) && size > 0) ? size : DEFAULT_FAB_SIZE
  const width = viewport && viewport.width
  const height = viewport && viewport.height
  const maxFor = (extent) => (typeof extent === 'number' && Number.isFinite(extent) && extent > 0)
    ? extent - s - FAB_EDGE_MARGIN
    : Infinity
  const clampAxis = (v, max) => Math.max(FAB_EDGE_MARGIN, Math.min(v, max))
  return {
    right: clampAxis(base.right, maxFor(width)),
    bottom: clampAxis(base.bottom, maxFor(height))
  }
}

// The chosen position after a drag: the position the drag STARTED from (what
// was on screen) moved by the pointer delta, then clamped into the window at
// that moment.  Offsets are floored at 0 BEFORE clamping: a pointer dragged
// past the right/bottom edge yields a negative offset, which
// clampFabPosition treats as garbage and would replace with the DEFAULT —
// the button would jump across the screen instead of stopping at the edge.
export function fabPositionFromDrag (start, dx, dy, viewport, size) {
  const from = isValidFabPosition(start) ? start : DEFAULT_FAB_POSITION
  const raw = {
    right: Math.max(0, from.right - (Number(dx) || 0)),
    bottom: Math.max(0, from.bottom - (Number(dy) || 0))
  }
  return clampFabPosition(raw, viewport, size)
}

// Read what the clamp needs from the live DOM: window size, the root font size
// (rem), and the button's own layout size.  offsetWidth is used rather than
// getBoundingClientRect because the button scales 110% on hover via a CSS
// transform, which the bounding rect includes and offsetWidth does not.
export function readFabViewport (button, win = typeof window !== 'undefined' ? window : undefined) {
  let rem = 16
  try {
    const fs = parseFloat(win.getComputedStyle(win.document.documentElement).fontSize)
    if (Number.isFinite(fs) && fs > 0) rem = fs
  } catch { /* no computed style (SSR / odd test env) — keep 16 */ }
  const measured = button && button.offsetWidth
  return {
    width: win ? win.innerWidth : undefined,
    height: win ? win.innerHeight : undefined,
    rem,
    size: (typeof measured === 'number' && measured > 0) ? measured : 3 * rem
  }
}

export function sameFabViewport (a, b) {
  return !!a && !!b && a.width === b.width && a.height === b.height &&
    a.rem === b.rem && a.size === b.size
}
